import type { MilestoneMap1 } from '@mm-shared/types/MilestoneMap1';
import type { ContinuousCode, DiscreteCode, Status1 } from '@mm-shared/types/Status1';
import { isValidContinuousCode } from '@mm-shared/types/statusTypeGuards';
import { getBackgroundColorClass } from '../../utils/colorUtils';
import { getBackgroundColorForResultCircles } from '../../utils/statusUtils';
import { getIntervalsForMilestoneMap } from '@mm-shared/intervals';

/**
 * cardStatus — the Implementation Plans board's colour derivation
 * (ADR.ImplementationPlansUX2 C1a–C1c).
 *
 * Everything here is a **pure** function of persisted data. Nothing reads
 * Redux, the clock or the DOM, and — the property the whole board depends on —
 * **nothing throws**. `getBackgroundColor`'s `default: throw` is one bad cell
 * in the grid; here the call sits inside `deriveCards`, inside a `useMemo`,
 * where a throw blanks the *whole board*. `Objective1.statuses` is typed
 * `Status1[]` but the type is not enforced at the Firestore boundary, so every
 * function below is total by construction over `string | null | undefined`.
 *
 * Deliberately NOT reused (ADR Alternative C): `getLatestStatusInInterval`
 * does not skip `'p'`/`''` placeholder rows, and bead `mr-t7pw` owns fixing the
 * shared helper. `utils/statusUtils.ts` and `utils/colorUtils.ts` are called
 * here and edited nowhere.
 */

/** The shape `getIntervalsForMilestoneMap` returns and `getStatusCellState` takes. */
export type CardStatusInterval = { start: Date; end: Date };

/** The two `null` cases of `cardStatusColorClass`, as human strings (C1b). */
export const PARENT_STATUS_LABEL = 'Parent objective — status shown on its own map';
export const NO_STATUS_LABEL = 'No status recorded';

const TOKEN_PREFIX = 'bg-status-';

/**
 * The objective's latest status **on the calendar timeline** (ADR C1a) — never
 * entry order, never `lastUpdatedDate`. Five rules, each a decision:
 *
 * 1. **Copy before sorting.** The argument is Redux state
 *    (`DescendantsTab.tsx`'s "fixes Redux state mutation"; the in-place bug is
 *    still live in `objective-utils.getLatestStatusCode`).
 * 2. **Drop unparseable `forIntervalEnding` rows FIRST.** `NaN` makes the
 *    comparator non-transitive, so one corrupt row would randomise *every*
 *    card's colour, not just its own.
 * 3. **Sort descending by `forIntervalEnding` epoch ms** — the calendar
 *    timeline, and the only ordering used.
 * 4. **Tie-break explicitly on array index, later wins.** Left to sort
 *    stability a tie keeps input order and `.find()` returns the
 *    *earliest*-entered row for that interval — the opposite of what a
 *    correction means. Entry order breaks ties; it never decides them.
 * 5. **Skip `''` only. `'p'` counts, like every other code** (UX2 C1a rule 5
 *    as amended by mr-60dv.14). `''` is a *cleared* cell — the user removed
 *    the status — and has no token of its own (`getBackgroundColor` maps it to
 *    `bg-gray-50`, which is not a status token). `'p'` is a *recorded*
 *    planning status the grid paints `bg-status-p`/`-i`/`-M`, so skipping it
 *    made the board disagree with the grid on every planned objective and made
 *    C1b's `'p'` arms dead code. `'?'` was never skipped and still is not.
 *
 *    The cost is named rather than hidden: a weekly map that auto-creates
 *    `'p'` rows for *future* intervals now paints those cards gray, because a
 *    future `'p'` really is the objective's calendar-latest recorded status
 *    and the grid's own latest cell really is gray. That is the price of grid
 *    parity, and it is a product decision (Bryan, PR #484 review) — see the
 *    ADR's Consequences and Alternative O.
 *
 * Future-dated statuses count: a non-placeholder future status is a forecast
 * its author recorded and the grid cell shows, and this board must agree with
 * the grid (ADR Alternative B).
 */
export function latestTimelineStatus(
  statuses: Status1[] | null | undefined,
): Status1 | null {
  if (!statuses || statuses.length === 0) return null;

  const dated = statuses
    .map((status, index) => ({
      status,
      index,
      time: new Date(status?.forIntervalEnding).getTime(),
    }))
    .filter((row) => Number.isFinite(row.time));

  dated.sort((a, b) => b.time - a.time || b.index - a.index);

  const latest = dated.find((row) => row.status.statusCode !== '');
  return latest ? latest.status : null;
}

/**
 * Code → `bg-status-*` token, **total**, at grid parity (ADR C1b). The branch
 * order mirrors `StatusCellPresenter.tsx`'s three-armed composition arm for
 * arm, so an objective the grid paints `bg-status-G` is painted `bg-status-G`
 * here:
 *
 * | Input | Result | Why |
 * |---|---|---|
 * | `objectiveIsParent` | `null` | the grid's first arm paints `''` — a parent's status lives on its own map (`DESCENDANTS_TAB.md`'s `mr-ihw.6` fix) |
 * | `null` / `undefined` / `''` | `null` | no reported status; also avoids `getBackgroundColor`'s `case '' → 'bg-gray-50'`, which is not a status token |
 * | `'p'` | routed like any other discrete code (below) | `'p'` is a *recorded* status, not the "no status" case — C1a stopped skipping it (mr-60dv.14) |
 * | a continuous code | `getBackgroundColorClass(code)` | metric objectives — `getBackgroundColor` *throws* on these (the shipped `Invalid status code: y12` crash) |
 * | `'G'`/`'R'`/`'Y'` | the token directly | `getBackgroundColor` throws on stored uppercase; the one mapping we cannot delegate |
 * | `'g'`/`'r'`/`'y'`/`'p'`/`'?'` | `getBackgroundColorForResultCircles(...)` | **the outer function the grid actually calls**, so an inchstone is `bg-status-p` here exactly as it is there |
 * | anything else | `null` | total by construction |
 *
 * `null` means **"this card carries no status colour"** — no rail, no
 * hairline, no tint, and the card wears the board's neutral **gray default**
 * (C1d, mr-60dv.14). The two ways to reach it are precisely the two cases the
 * grid also leaves uncoloured. It is emphatically *not* the `'p'` case: a
 * planned objective is a card with a gray **rail**, and the rail is what
 * separates "planning was recorded" from "nothing was recorded".
 *
 * `hasResults`/`hasKeyResults` must be **the grid cell's** — taken over the
 * interval *containing* the latest status at the map's base grain, not over
 * the whole objective (C1c, `deriveCards`).
 */
export function cardStatusColorClass(
  statusCode: DiscreteCode | ContinuousCode | string | null | undefined,
  hasResults: boolean,
  hasKeyResults: boolean,
  objectiveIsParent: boolean,
): string | null {
  if (objectiveIsParent) return null;
  if (statusCode === null || statusCode === undefined || statusCode === '') return null;

  if (isValidContinuousCode(statusCode)) {
    return getBackgroundColorClass(statusCode as ContinuousCode);
  }

  // Tailwind's JIT needs the class as a literal in source, so these are written
  // out rather than interpolated from the code.
  switch (statusCode) {
    case 'G':
      return 'bg-status-G';
    case 'R':
      return 'bg-status-R';
    case 'Y':
      return 'bg-status-Y';
    case 'g':
    case 'r':
    case 'y':
    case 'p':
    case '?': {
      const token = getBackgroundColorForResultCircles(
        statusCode,
        hasResults,
        hasKeyResults,
      );
      // Defensive: the five codes above can never reach `getBackgroundColor`'s
      // `case '' → 'bg-gray-50'` arm, but the board's contract is "a status
      // token or null", never a non-token class.
      return token.startsWith(TOKEN_PREFIX) ? token : null;
    }
    default:
      return null;
  }
}

/**
 * The human string for the card's accessible name and `title` (ADR C1f),
 * reusing `DescendantsTab.tsx`'s switch plus the two `null` cases — which are
 * distinguished, because "this objective has no status" and "this objective's
 * status lives on another map" are different facts.
 *
 * Since mr-60dv.13 removed the visible glyph this is the **only** carrier of
 * the status as text: it names the node wrapper (`ariaLabel`) and the card
 * root's `title`. Both call sites are load-bearing rather than belt-and-braces
 * — the board's accepted 1.4.1 posture is the grid's own (colour, plus codes
 * only behind the grid's `showStatusCodes` toggle), so the *only* textual
 * route to a card's status is this string.
 */
export function cardStatusLabel(
  statusCode: DiscreteCode | ContinuousCode | string | null | undefined,
  hasResults: boolean,
  hasKeyResults: boolean,
  objectiveIsParent: boolean,
): string {
  if (objectiveIsParent) return PARENT_STATUS_LABEL;
  if (
    cardStatusColorClass(statusCode, hasResults, hasKeyResults, objectiveIsParent) === null
  ) {
    return NO_STATUS_LABEL;
  }

  const code = statusCode as string;
  if (isValidContinuousCode(code)) {
    const letter = code[0];
    if (letter === 'g') return 'Green';
    if (letter === 'y') return 'Yellow';
    if (letter === 'r') return 'Red';
  }

  switch (code) {
    case 'g':
    case 'G':
      return 'Green';
    case 'y':
    case 'Y':
      return 'Yellow';
    case 'r':
    case 'R':
      return 'Red';
    case 'p':
      return 'Pending';
    case '?':
      return 'Unknown';
    default:
      return NO_STATUS_LABEL;
  }
}

/**
 * The map's intervals at its **own** grain (ADR C1c). Two decisions:
 *
 * - **The grain is pinned to `milestoneMap.interval`.** `effectiveZoomLevel` is
 *   UI state the pure derivation cannot see and must not (ADR B1) — and the
 *   pin is not arbitrary: the grid's own `effectiveZoomLevel` is
 *   `zoomLevel ?? milestoneMap.interval`, so the board reproduces the grid
 *   whenever the grid is not zoomed, which is its default and the state of
 *   every `?zoomLevel=`-less load.
 * - **It cannot throw.** `getIntervalsForMilestoneMap` ends in
 *   `default: throw`, and this moves it out of a click handler into a
 *   render-time `useMemo` where an unhandled `interval` would blank the whole
 *   wrapper. An empty array simply means every card falls to `false`/`false`.
 */
export function cardStatusIntervals(map: MilestoneMap1): CardStatusInterval[] {
  try {
    return getIntervalsForMilestoneMap(map, map.interval);
  } catch {
    return [];
  }
}

/**
 * Done means every key result is delivered; a card with none is NOT done
 * (ADR.ImplementationPlansUX2 C3). A pure predicate over the card's own
 * `keyResults` — the same "derive, don't store" shape as `cardHasAdditionalInfo`
 * (A1d) — rather than a field, so it can never drift from what the checkboxes
 * on the card actually show.
 *
 * **Non-key results are ignored, by construction**: `deriveCards` filters
 * `isKeyResult` before the card exists (`planLayout.ts`), so `card.keyResults`
 * already excludes them — "what you see decides whether it disappears" is the
 * only rule a user can hold.
 *
 * **Zero key results is NOT done** — the `length > 0` conjunct, over vacuous
 * truth. An objective that never had a key result has delivered nothing;
 * vacuous truth would hide precisely the objectives nobody has scoped yet; and
 * it is deletion-shaped — deleting the last key result would make the card
 * vanish at the moment the user removed the evidence of completion.
 *
 * Takes a structural `{ keyResults }` shape rather than importing `PlanCard`
 * from `../types` — that module imports `PlanCardView` from `planLayout.ts`,
 * which imports this module, so a `PlanCard` import here would be a cycle.
 */
export function isCardDone(card: {
  keyResults: ReadonlyArray<{ delivered: boolean }>;
}): boolean {
  return card.keyResults.length > 0 && card.keyResults.every((kr) => kr.delivered);
}

/**
 * The interval **containing** a date — `iv.start < d <= iv.end`, the grid's own
 * rule (`getLatestStatusInInterval` → `isPointInInterval(…, false, true)`).
 *
 * Containment rather than an exact `iv.end` key (ADR Alternative P3): zoom a
 * quarterly map to months and set a status on the January cell, and
 * `forIntervalEnding = 2026-01-31T…` matches **no** quarter's end. The unzoomed
 * grid still paints the Q1 cell from it; an exact-key lookup misses and paints
 * a different colour than the cell it claims to mirror.
 *
 * The array is ascending, so this is a binary search: O(log n) per card rather
 * than a scan. A date at or before the first `start`, after the last `end`, or
 * in a gap between two intervals resolves to `undefined` — the caller reads
 * that as `false`/`false`, never as the map's last interval
 * (`calculateIntervalRange`'s fallback is deliberately *not* mirrored: it would
 * attribute one interval's results to another).
 *
 * **The left walk is not decoration.** Every grain but `'hours'` produces
 * disjoint intervals (`23:59:59.999` → `00:00:00.000`), but `getIntervalEnd`
 * pushes an hour interval's end to `+1h59m59.999s` — the "duplicate results"
 * TODO in `intervalUtils.ts` — so consecutive hours OVERLAP and a bare binary
 * search could land on the second of two containing intervals. Walking left
 * while the predecessor also contains the date makes the answer identical to a
 * linear first-match scan for **every** grain, at a bounded cost. Fixing the
 * overlap itself belongs to `intervalUtils`, which this round does not touch.
 */
export function intervalContaining(
  intervals: readonly CardStatusInterval[],
  date: Date,
): CardStatusInterval | undefined {
  const time = date.getTime();
  if (!Number.isFinite(time) || intervals.length === 0) return undefined;

  const contains = (interval: CardStatusInterval) =>
    time > interval.start.getTime() && time <= interval.end.getTime();

  let low = 0;
  let high = intervals.length - 1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    const interval = intervals[mid];
    if (time <= interval.start.getTime()) {
      high = mid - 1;
    } else if (time > interval.end.getTime()) {
      low = mid + 1;
    } else {
      let first = mid;
      while (first > 0 && contains(intervals[first - 1])) first--;
      return intervals[first];
    }
  }
  return undefined;
}
